import Divider from "@components/atoms/divider";
import { Box, Link, Typography } from "@mui/material";
import { ReactElement, useState } from "react";

import {
  CardActionWrapper,
  CardText,
  CardTitle,
  Container,
  Content,
  CurrencyWrapper,
  DateWrapper,
  DetailFooterWrapper,
  FeesAndCurrencyWrapper,
  Footer,
  FooterInfo,
  FooterWrapper,
  GameDetailTypography,
  Header,
  IconWrapper,
  Info,
  InfoContainer,
  InfoWrapper,
  Rating,
  RatingMessage,
  RatingWrapper,
  ResultWrapper,
  Score,
  TitleWrapper,
  UnRatedMessage,
  ViewScoreWrapper,
  Wrapper,
} from "./finshedGameDetailsCard.styled";
import { ComponentProps } from "./finshedGameDetailsCard.type";

import { ReactComponent as Calendar } from "@assets/images/calender/calendar.svg";
import { ReactComponent as Cash } from "@assets/images/cash/BsCash.svg";
import { ReactComponent as Chat } from "@assets/images/chat/chat-bubble-left-ellipsis.svg";
import { ReactComponent as ChevronDown } from "@assets/images/chevrons/chevron-down.svg";
import { ReactComponent as ChevronUp } from "@assets/images/chevrons/chevron-up.svg";
import { ReactComponent as USD } from "@assets/images/currencies/BsCurrencyDollar.svg";
import { ReactComponent as EUR } from "@assets/images/currencies/BsCurrencyEuro.svg";
import { ReactComponent as GBP } from "@assets/images/currencies/BsCurrencyPound.svg";
import { ReactComponent as AED } from "@assets/images/currencies/trade 1.svg";
import { ReactComponent as PinMap } from "@assets/images/pin-map/map-pin.svg";
import { ReactComponent as Star } from "@assets/images/star/star.svg";
import { ReactComponent as UnActiveStar } from "@assets/images/star/un-active-star.svg";

const FinishedGameDetailsCard = ({
  date,
  fee,
  location,
  title,
  scoreA,
  scoreB,
  info,
  link,
  currency,
  gameRate,
  sideATitle,
  sideBTitle,
  handleEdit,
}: ComponentProps) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);

  const InfoRender = (icon: ReactElement, title?: string, isDate?: boolean) => {
    const date = isDate && title?.split(",");

    return (
      <Info>
        <Box>{icon}</Box>
        <>
          {isDate ? (
            <DateWrapper>
              <CardText>{`${date && date[0]}`}</CardText>
              <CardText>{`${date && date[1]}`}</CardText>
            </DateWrapper>
          ) : (
            <CardText>{title}</CardText>
          )}
        </>
      </Info>
    );
  };

  const CurrencyPriceRender = (icon: ReactElement, title: string | number) => {
    return (
      <Info>
        <Box>{icon}</Box>
        <CurrencyWrapper isPound={currency === "GBP"}>
          <CardText sx={{ wordWrap: "break-word" }}>{title}</CardText>
          {currencyRender(currency)}
        </CurrencyWrapper>
      </Info>
    );
  };

  const currencyRender = (currency: string) => {
    switch (currency) {
      case "USD":
        return <USD width={15} height={15} />;
      case "AED":
        return <AED width={15} height={15} />;
      case "GBP":
        return <GBP width={15} height={15} />;
      case "EUR":
        return <EUR width={15} height={15} />;
      default:
        break;
    }
  };

  function shortenUrlToLength(originalUrl: string, length: number) {
    if (originalUrl.length <= length) {
      return originalUrl;
    }

    // Extract the protocol and domain
    const url = new URL(originalUrl);
    const protocolDomain = url.protocol + "//" + url.host;

    // Calculate the available space for the path and query
    const availableSpace = length - protocolDomain.length - 3; // 3 for "..."

    // Get the path and query
    let pathQuery = url.pathname + url.search;

    // Shorten the path and query if necessary
    if (pathQuery.length > availableSpace) {
      pathQuery = pathQuery.slice(0, availableSpace) + "...";
    }

    // Reconstruct the shortened URL
    const shortenedUrl = protocolDomain + pathQuery;

    return shortenedUrl;
  }

  const FooterInfoRender = (
    icon: ReactElement,
    title: string | number,
    isLink: boolean
  ) => {
    return (
      <FooterInfo>
        <Box>{icon}</Box>
        {isLink ? (
          <Link
            sx={{ wordWrap: "break-word", cursor: "pointer" }}
            onClick={() => {
              window.open(link, "_blank");
            }}
          >
            {shortenUrlToLength(link!, 30)}
          </Link>
        ) : (
          <Typography
            sx={{
              wordWrap: "break-word",
              fontFamily: "NotoSans-Medium",
              fontSize: "14px",
            }}
          >
            {title}
          </Typography>
        )}
      </FooterInfo>
    );
  };

  const handleFooterClick = () => {
    setExpanded(!isExpanded);
  };

  return (
    <Container>
      <Header>
        <TitleWrapper>
          <CardTitle>{title}</CardTitle>
        </TitleWrapper>
      </Header>
      <Content>
        <ResultWrapper>
          <CardActionWrapper>
            <Wrapper>
              <ViewScoreWrapper>
                <Typography>{sideATitle}</Typography>
                <Score>{scoreA}</Score>
              </ViewScoreWrapper>
              <Typography>-</Typography>
              <ViewScoreWrapper>
                <Score>{scoreB}</Score>
                <Typography>{sideBTitle}</Typography>
              </ViewScoreWrapper>
            </Wrapper>
          </CardActionWrapper>
        </ResultWrapper>
        <>
          {gameRate && gameRate.user_rating > 0 ? (
            <RatingWrapper>
              <Star />
              {gameRate.average_rating && (
                <Rating>{`Game Rating ${gameRate.average_rating}.0`}</Rating>
              )}
              {gameRate.user_rating && (
                <RatingMessage>{`(You rated this game ${gameRate.user_rating}/5)`}</RatingMessage>
              )}
            </RatingWrapper>
          ) : (
            <RatingWrapper>
              <UnActiveStar />
              <UnRatedMessage>{`You haven't rated this game yet`}</UnRatedMessage>
            </RatingWrapper>
          )}
        </>
        <Footer onClick={handleFooterClick}>
          {isExpanded ? (
            <DetailFooterWrapper>
              <InfoContainer>
                {InfoRender(<Calendar />, date, true)}
                <FeesAndCurrencyWrapper>
                  {CurrencyPriceRender(<Cash />, fee.toString())}
                </FeesAndCurrencyWrapper>
                {InfoRender(<PinMap />, location)}
              </InfoContainer>
              <InfoWrapper>
                {link && FooterInfoRender(<Chat />, link, true)}
                {info && <Typography>{info}</Typography>}
              </InfoWrapper>
              <br />
              <IconWrapper>
                <ChevronUp />
              </IconWrapper>
            </DetailFooterWrapper>
          ) : (
            <>
              <Divider orientation="horizontal" />
              <FooterWrapper>
                <GameDetailTypography>{"Game details"}</GameDetailTypography>
                <IconWrapper>
                  <ChevronDown />
                </IconWrapper>
              </FooterWrapper>
            </>
          )}
        </Footer>
      </Content>
    </Container>
  );
};

export default FinishedGameDetailsCard;
